/**
 * Extracted from quick-add-offer-picker.tsx (CU-86cb8grgr) to keep that file under the
 * repo's max-lines lint threshold: the inline "creating"-mode form's Zod schema and its
 * `useForm` wiring, including the optional campaign-duration fields
 * (defaultDateFrom/defaultDateTo) that default from a calendar-driven caller's selection.
 */
import * as React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useWatch, type UseFormReturn } from 'react-hook-form';
import { z } from 'zod';
import type { Client, SalesUser } from '@ooh/contracts';
import { END_AFTER_START_MESSAGE } from '@ooh/contracts';
import {
  resolveSalespersonGating,
  useSalespersonSelfDefault,
} from '../../offers/lib/salesperson-gating';
import {
  applyAutoSelectedBrand,
  isBrandStillValid,
  useInlineClientCreate,
  type ClientOption,
} from '../../offers/lib/brand-inline-client';
import { computeCampaignNumberPreviewInput } from '../../offers/lib/campaign-number-preview-input';
import type { QuickAddClientOption } from './quick-add-offer-picker';

export const NEW_CLIENT_OPTION_VALUE = '__new__';

const createDraftFormSchema = z
  .object({
    clientId: z
      .string()
      .min(1, 'clientRequired')
      .refine((value) => value !== NEW_CLIENT_OPTION_VALUE, 'clientRequired'),
    salespersonId: z.number().optional(),
    brand: z.string().nullable().optional(),
    defaultDateFrom: z.date().nullable().optional(),
    defaultDateTo: z.date().nullable().optional(),
  })
  .superRefine((value, ctx) => {
    if (
      value.defaultDateFrom instanceof Date &&
      value.defaultDateTo instanceof Date &&
      value.defaultDateTo < value.defaultDateFrom
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: END_AFTER_START_MESSAGE,
        path: ['defaultDateTo'],
      });
    }
  });

export type QuickAddCreateDraftFormValues = z.infer<typeof createDraftFormSchema>;

export interface UseQuickAddCreateDraftFormArgs {
  inlineClientValue?: string;
  initialDefaultDateFrom?: Date | null;
  initialDefaultDateTo?: Date | null;
  salesUsers: SalesUser[];
  authUserId?: number | null;
  /**
   * Whether the caller holds offer.assign_other_salesperson. Passed as a
   * plain prop (not resolved via useHasPermission inside this hook) for the
   * same apps/mockups "no router/query context" reason as salesUsers/
   * authUserId — see quick-add-offer-picker.tsx's QuickAddOfferPickerProps
   * doc comment. Defaults to true (fail-open) when omitted.
   */
  canAssignOtherSalesperson?: boolean;
  clientOptions: QuickAddClientOption[];
}

/**
 * Extracted from `useQuickAddCreateDraftForm` to keep that hook under the
 * repo's max-lines-per-function lint threshold: composes the campaign-number
 * preview's query input from the client-merge result and the already-watched
 * brand/salesperson values, via the shared computeCampaignNumberPreviewInput
 * (offer-draft-numbering / "Campaign-number preview on offer creation
 * forms") — see that function's own doc comment for the null-until-complete
 * rule and the stale-brand guard. null propagates through to
 * QuickAddOfferPicker, which reports it to its caller; that caller feeds it
 * into useCampaignNumberPreview, since this hook itself stays free of any
 * query/router dependency (see quick-add-offer-picker.tsx).
 */
function useQuickAddCampaignNumberPreviewInput(
  selectedClient: ClientOption | undefined,
  brand: string | null | undefined,
  selectedSalesperson: SalesUser | undefined,
) {
  return React.useMemo(
    () =>
      computeCampaignNumberPreviewInput({
        clientId: selectedClient ? Number(selectedClient.id) : null,
        clientAcronym: selectedClient?.acronym ?? null,
        clientBrands: selectedClient?.brands,
        brand,
        salespersonName: selectedSalesperson?.name ?? null,
      }),
    [selectedClient, brand, selectedSalesperson],
  );
}

/** Extracted so its own `||`/`&&` chain doesn't count towards the hook's complexity. */
function computeIsConfirmDisabled(
  clientId: string,
  salespersonGateDisabled: boolean,
  isSubmitting: boolean,
): boolean {
  return (
    !clientId || clientId === NEW_CLIENT_OPTION_VALUE || salespersonGateDisabled || isSubmitting
  );
}

/** Extracted purely to keep `useQuickAddInlineClient` under the function line-count threshold. */
function toClientOption(option: QuickAddClientOption): ClientOption {
  return {
    id: option.value,
    name: option.label,
    brands: option.brands ?? [],
    acronym: option.acronym ?? '',
  };
}

/** Extracted purely to keep `useQuickAddInlineClient` under the function line-count threshold. */
function toQuickAddClientOption(client: ClientOption): QuickAddClientOption {
  return { label: client.name, value: client.id, brands: client.brands, acronym: client.acronym };
}

/**
 * Extracted from `useQuickAddCreateDraftForm` to keep that hook under the
 * repo's max-lines-per-function lint threshold: the inline "creating"-mode
 * form's client-merge (`useInlineClientCreate`) and brand-clearing wiring,
 * mirroring `create-offer-page.tsx`'s equivalent logic.
 */
function useQuickAddInlineClient(
  form: UseFormReturn<QuickAddCreateDraftFormValues>,
  clientId: string,
  clientOptions: QuickAddClientOption[],
) {
  const baseClients: ClientOption[] = React.useMemo(
    () =>
      clientOptions
        .filter((option) => option.value !== NEW_CLIENT_OPTION_VALUE)
        .map(toClientOption),
    [clientOptions],
  );
  const { clients, handleClientCreated } = useInlineClientCreate<ClientOption>(baseClients);
  const selectedClient = React.useMemo(
    () => clients.find((client) => client.id === clientId),
    [clients, clientId],
  );

  React.useEffect(() => {
    const currentBrand = form.getValues('brand');
    if (isBrandStillValid(currentBrand, selectedClient?.brands)) return;
    form.setValue('brand', null, { shouldDirty: true, shouldValidate: true });
  }, [form, selectedClient]);

  const clientOptionsWithCreated: QuickAddClientOption[] = React.useMemo(
    () => clients.map(toQuickAddClientOption),
    [clients],
  );

  const handleClientDialogResult = React.useCallback(
    (client: Client, wasCreated: boolean) => {
      handleClientCreated({
        id: String(client.id),
        name: client.name,
        brands: client.brands,
        acronym: client.acronym,
      });
      form.setValue('clientId', String(client.id), { shouldDirty: true, shouldValidate: true });
      form.clearErrors('clientId');
      applyAutoSelectedBrand(client.brands, wasCreated, (brand) =>
        form.setValue('brand', brand, { shouldDirty: true, shouldValidate: true }),
      );
    },
    [form, handleClientCreated],
  );

  return {
    clientOptions: clientOptionsWithCreated,
    selectedClient,
    selectedClientBrands: selectedClient?.brands ?? [],
    onClientCreated: handleClientDialogResult,
  };
}

export function useQuickAddCreateDraftForm(args: UseQuickAddCreateDraftFormArgs) {
  const form = useForm<QuickAddCreateDraftFormValues>({
    defaultValues: {
      clientId:
        args.inlineClientValue != null && args.inlineClientValue !== NEW_CLIENT_OPTION_VALUE
          ? args.inlineClientValue
          : '',
      salespersonId: undefined,
      defaultDateFrom: args.initialDefaultDateFrom ?? null,
      defaultDateTo: args.initialDefaultDateTo ?? null,
    },
    resolver: zodResolver(createDraftFormSchema),
  });
  const clientId = useWatch({ control: form.control, name: 'clientId' });
  const salespersonId = useWatch({ control: form.control, name: 'salespersonId' });
  const brand = useWatch({ control: form.control, name: 'brand' });
  const {
    formState: { isSubmitting },
  } = form;

  const {
    isSalesDepartment,
    submitDisabled: salespersonGateDisabled,
    fieldDisabled: salespersonFieldDisabled,
  } = resolveSalespersonGating(
    args.salesUsers,
    args.authUserId,
    salespersonId,
    false,
    args.canAssignOtherSalesperson,
  );

  useSalespersonSelfDefault(form, isSalesDepartment, args.authUserId);

  // Merges args.clientOptions with any client created/selected inline via
  // BrandInlineClientField's ClientDialog (offer-client-collaboration,
  // CU-86cbc2qv8) so a newly created client is selectable immediately,
  // mirroring create-offer-page.tsx's useInlineClientCreate usage.
  const inlineClient = useQuickAddInlineClient(form, clientId, args.clientOptions);

  const selectedSalesperson = args.salesUsers.find((user) => user.id === salespersonId);
  const campaignNumberPreviewInput = useQuickAddCampaignNumberPreviewInput(
    inlineClient.selectedClient,
    brand,
    selectedSalesperson,
  );

  return {
    form,
    // isSubmitting stays true for the full duration of the RHF handleSubmit
    // async callback -- including the await on props.onConfirm's mutateAsync
    // chain -- so this also guards against a rapid re-click or Enter-key
    // repeat firing a duplicate createOffer request (CU-86camj99f).
    isConfirmDisabled: computeIsConfirmDisabled(clientId, salespersonGateDisabled, isSubmitting),
    salespersonFieldDisabled,
    campaignNumberPreviewInput,
    ...inlineClient,
  };
}

export type UseQuickAddCreateDraftFormReturn = ReturnType<typeof useQuickAddCreateDraftForm>;
